import { updateAlert } from '../alerts/alerts';
import { appointedd } from '../appointedd/appointedd';
import { fetchText } from '../fetch-helper/fetch-helper';
import { openPanel, slideoutPanel, updatePanel } from '../slideout-panel/slideout-panel';
import { gtag } from '../utility-functions/utility-functions';

window.outfitbuilder = window.outfitbuilder || {};
let outfitbuilder = window.outfitbuilder;

/**
 * Initializes the Book an Appointment page functionality
 * @remarks Finds all elements with the class 'js-outfitbuilder-instore-appointment' and adds a click event listener to each of them.
 * When clicked, it prevents the default action and calls the handleBookAppointmentClick function.
 * @param store - Boolean indicating if the appointment is for a store (default: false)
 * @returns {void}
 */
export function bookAppointmentSlideout(store = false): void {
  const appointments = document.querySelectorAll<HTMLElement>('.js-outfitbuilder-instore-appointment');
  if (appointments.length > 0) {
    appointments.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        handleBookAppointmentClick(button, store);
      });
    });
  }
};

/**
 * Handles the click event for booking an appointment slideout.
 * @remarks If the button has a data attribute 'data-save', it saves the outfit before opening the slideout.
 * If the button is for a store, it calls the appointedd function.
 * Otherwise, it opens the slideout panel with the content fetched from the server.
 * @param button - The button element that was clicked.
 * @param store - Boolean indicating if the appointment is for a store (default: false)
 * @returns {void}
 */
export function handleBookAppointmentClick(button: HTMLElement, store = false): void {
  if (store) {
    appointedd(button);
    return;
  } else {
    if (button.dataset.save) {
      outfitbuilder.saveOutfit();
    }

    openPanel('Store Appointments', fetchAppointmentSlideoutContent());
    slideoutPanel();
  }
}

/**
 * Fetches the appointment slideout content from the server and updates the panel with the response.
 * @returns {string} - Returns a string indicating the loading status of the store appointments.
 */
export const fetchAppointmentSlideoutContent = (): string => {
  const url = `/cm/api/lookup`;
  const data = fetchText(url, null);

  data.then(data => {
    updatePanel('Store Appointments', data);
    handleStoreLookupEvents();
  });

  return 'Loading store appointments...'; // Return initial loading message
}

/**
 * Handles the store lookup events by adding a submit event listener to the search form.
 * @remarks When the form is submitted, it prevents the default action, collects the form data, and calls the storeSearch function with the postcode.
 * @returns {void}
 */
const handleStoreLookupEvents = (): void => {
  const search = document.querySelector('.js-store-search') as HTMLFormElement;
  if (search) {
    search.addEventListener('submit', (e) => {
      e.preventDefault();

      const data = new FormData(search);
      const formObject: any = {};

      data.forEach((value, key) => {
        formObject[key] = value;
      });

      formObject["draft"] = outfitbuilder

      if (formObject.postcode !== "") {
        outfitbuilder.storeSearch(formObject.postcode);

        gtag('event', 'store_finder', {
          'search_term': formObject.postcode
        });

      } else {
        updateAlert('Lookup', '', 'Postcode field is missing.', 'error');

        gtag('event', 'failed_store_search', {
          'search_term': formObject.postcode,
          'failure_reason': 'Postcode field is missing.'
        });
      }
    });
  }
}

/**
 * Fetches the store search results based on the provided postcode.
 * @param postcode - The postcode to search for stores.
 * @remarks It constructs a URL with the postcode and draft order number, fetches the HTML content from the server, and updates the store list in the DOM.
 * @returns {void}
 */
outfitbuilder.storeSearch = function (postcode: string): void {
  const id = outfitbuilder.DraftOrderNumber ? outfitbuilder.DraftOrderNumber : "";
  const url = "/custom-made/storesearch?postcode=" + postcode + "&draft=" + id + "&tailor-me=true";
  const response = fetchText(url, null);

  response.then((html) => {
    const dynamicContent = document.querySelector('.stores-lookup__stores');

    if (dynamicContent) {
      dynamicContent.innerHTML = html;
      outfitbuilder.handleStoreListEvents();
    }
  }).catch((error) => {
    console.error('Error fetching store search results:', error);
    updateAlert('Store Search', '', 'Failed to fetch store search results.', 'error');
  });
};

/**
 * Handles the store list events by adding click event listeners to the store items.
 * @remarks When a store item is clicked, it toggles the 'active' class on the store item and calls the appointedd function.
 * It also initializes the appointedd functionality for the store list.
 * @returns {void}
 */
outfitbuilder.handleStoreListEvents = (): void => {
  const stores = document.querySelectorAll<HTMLElement>('.stores-list__item');
  stores.forEach((store) => {
    store.querySelector('.js-store-read-more').addEventListener('click', () => {
      store.classList.toggle('active');
    });
  });
  appointedd();
};
